import { Observable } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { select, Store } from '@ngrx/store';
import { IRemoteCartReadService } from '~shared/modules/cart/services/remote-cart.interface';
import { ICart, ShoppingCartError } from '~shared/modules/cart/models';
import { DiscountCode } from '~app/modules/checkout/models';
import { ApiConfig, ORDER_STEP_REVIEW_LINK } from '~shared/constants';
import { AccountInfoService } from '~core/modules/identity/services/account-info.service';
import { handleError } from '~shared/modules/cart/utils/handle-error';
import { CheckoutCommonUtil } from '~app/modules/checkout/utils/checkout-common-util';
import { CheckoutAddressService } from '~app/modules/checkout/services/checkout-address.service';
import * as fromRouter from '~core/store';
import { storeValuesValidator } from '~shared/utils/store-values-validator.util';

const DISCOUNT_MIN_SPENDING_AMOUNT_ERROR = 'DISCOUNT.MIN_SPENDING_AMOUNT';
const DISCOUNT_CODE_FIELD = 'discountCode';

@Injectable()
export class RemoteAppCartReadService implements IRemoteCartReadService {
  constructor(
    private accountInfoService: AccountInfoService,
    private readonly checkoutAddressService: CheckoutAddressService,
    private readonly httpClient: HttpClient,
    private readonly routerStore: Store<fromRouter.RouterState>
  ) {}

  read(): Observable<ICart | ShoppingCartError> {
    return this.readWithOptionalDiscount();
  }

  readWithDiscount(discountCode: string): Observable<DiscountCode | ShoppingCartError> {
    return this.routerStore.pipe(
      select(fromRouter.getRouterState),
      take(1),
      switchMap((routeState) => {
        const isReviewPage = routeState?.url?.includes(ORDER_STEP_REVIEW_LINK);

        return isReviewPage
          ? this.readCartReview(discountCode)
          : this.readWithOptionalDiscount(discountCode).pipe(map((cart) => this.mapCartToDiscountResponse(cart)));
      })
    );
  }

  readCartReview(discountCode: string | null = null): Observable<DiscountCode | ShoppingCartError> {
    return this.checkoutAddressService.getSelectedAddresses().pipe(
      switchMap((address) => {
        const params = {
          billingAddress: address.billingAddress,
          shippingAddress: address.shippingAddress,
          discountCode,
          organizationVatNumber: CheckoutCommonUtil.getTmpVatIdFromSessionStorage(),
          freightForwardPhone: address.freightForwardingPhone,
        };

        storeValuesValidator([address.billingAddress, address.shippingAddress]);

        return this.httpClient.post<DiscountCode>(ApiConfig.BUYER_REACT_ORDER_REVIEW_URL(), params).pipe(
          map((response) => this.mapCartToDiscountResponse(response.cart)),
          catchError(handleError)
        );
      })
    );
  }

  private readWithOptionalDiscount(discountCode: string | null = null): Observable<ICart | ShoppingCartError> {
    return this.accountInfoService.getAccountInfo().pipe(
      switchMap((accountInfo) => {
        const baseUrl = ApiConfig.SVC_BUYER_GATEWAY_URL_API_ENDPOINT();
        const discountQuery = !!discountCode ? `?discountCode=${discountCode}` : '';
        const metroCardQuery =
          !!discountCode && !!accountInfo.metroCard ? `&metroCardNumber=${accountInfo.metroCard}` : '';
        const url = `${baseUrl}/api/account/proxy/cart/v1/carts/my${discountQuery}${metroCardQuery}`;
        const httpOptions = {
          headers: new HttpHeaders({
            'Cache-Control': 'no-store',
            'X-Active-Context': accountInfo.activeContext,
          }),
        };

        return this.httpClient.get<ICart>(url, httpOptions).pipe(catchError(handleError));
      })
    );
  }

  private mapCartToDiscountResponse(cart: ICart | ShoppingCartError): DiscountCode | ShoppingCartError {
    if (cart instanceof ShoppingCartError) {
      return cart;
    }

    const discountError = cart?.invalidParams?.find((invalidParam) => invalidParam?.field === DISCOUNT_CODE_FIELD);

    const discountMinSpendingAmountError = cart?.invalidParams?.find(
      (invalidParam) => invalidParam?.code === DISCOUNT_MIN_SPENDING_AMOUNT_ERROR
    );

    return {
      cart,
      hasAlcohol: !!cart?.features?.hasAlcohol,
      applied: !!cart?.features?.discountCode,
      code: cart?.features?.discountCode,
      value: discountMinSpendingAmountError?.details?.[0]?.reason,
      error: discountError?.code?.replace('DISCOUNT.', '').toLowerCase(),
    };
  }
}
